// apps/web/src/components/WYSIWYGTemplateBuilder.js
import React, { useState } from 'react';

/**
 * WYSIWYG Template Builder
 * Visual drag-and-drop interface for creating interactive templates
 */
export default function WYSIWYGTemplateBuilder({ onSave, initialData }) {
  const [variables, setVariables] = useState(initialData?.variables || []);
  const [tasks, setTasks] = useState(initialData?.tasks || []);
  const [selectedVariable, setSelectedVariable] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [draggedComponent, setDraggedComponent] = useState(null);

  // Auto-save whenever variables or tasks change
  React.useEffect(() => {
    if (variables.length > 0 || tasks.length > 0) {
      const templateConfig = {
        interactive: true,
        visual: true,
        variables: variables.map(v => {
          const clean = { ...v };
          delete clean.id;
          return clean;
        }),
        tasks: tasks.map(t => {
          const clean = { ...t };
          delete clean.id;
          return clean;
        }),
      };
      onSave(templateConfig);
    }
  }, [variables, tasks, onSave]);

  // Component library
  const componentTypes = [
    { type: 'text', label: 'Text Input', icon: '📝' },
    { type: 'number', label: 'Number', icon: '🔢' },
    { type: 'date', label: 'Date Picker', icon: '📅' },
    { type: 'dropdown', label: 'Dropdown', icon: '📋' },
    { type: 'checkbox', label: 'Checkbox', icon: '☑️' },
    { type: 'checkbox_group', label: 'Checkbox Group', icon: '✅' },
  ];

  // Add new variable from drag
  function handleDropVariable(e) {
    e.preventDefault();
    if (!draggedComponent) return;

    const newVariable = {
      id: `field_${Date.now()}`,
      type: draggedComponent.type,
      label: `New ${draggedComponent.label}`,
      required: false,
      ...getDefaultPropsForType(draggedComponent.type),
    };

    setVariables([...variables, newVariable]);
    setSelectedVariable(newVariable);
    setSelectedTask(null); // Clear task selection
    setDraggedComponent(null);
  }

  function getDefaultPropsForType(type) {
    switch (type) {
      case 'number':
        return { min: 0, max: 100, default: 0 };
      case 'dropdown':
        return { options: ['Option 1', 'Option 2', 'Option 3'] };
      case 'checkbox_group':
        return { options: ['Choice 1', 'Choice 2', 'Choice 3'] };
      case 'checkbox':
        return { default: false };
      case 'text':
        return { placeholder: '' };
      default:
        return {};
    }
  }

  function updateVariable(id, updates) {
    // If updating the ID, check for duplicates
    if (updates.id && updates.id !== id) {
      const isDuplicate = variables.some(v => v.id !== id && v.id === updates.id);
      if (isDuplicate) {
        alert(`Field ID "${updates.id}" is already in use. Please choose a unique ID.`);
        return;
      }
    }

    setVariables(variables.map(v => v.id === id ? { ...v, ...updates } : v));
    if (selectedVariable?.id === id) {
      setSelectedVariable({ ...selectedVariable, ...updates });
    }
  }

  function deleteVariable(id) {
    setVariables(variables.filter(v => v.id !== id));
    if (selectedVariable?.id === id) {
      setSelectedVariable(null);
    }
  }

  function addTask() {
    const newTask = {
      id: `task_${Date.now()}`,
      title: 'New Task',
      description: '',
      status: 'todo',
      condition: '',
    };
    setTasks([...tasks, newTask]);
    setSelectedTask(newTask);
    setSelectedVariable(null); // Clear variable selection
  }

  function updateTask(id, updates) {
    setTasks(tasks.map(t => t.id === id ? { ...t, ...updates } : t));
    if (selectedTask?.id === id) {
      setSelectedTask({ ...selectedTask, ...updates });
    }
  }

  function deleteTask(id) {
    setTasks(tasks.filter(t => t.id !== id));
    if (selectedTask?.id === id) {
      setSelectedTask(null);
    }
  }

  function insertVariableIntoTask(variableId) {
    if (!selectedTask) return;
    const currentTitle = selectedTask.title || '';
    updateTask(selectedTask.id, {
      title: currentTitle + `{{${variableId}}}`,
    });
  }

  return (
    <div className="WYSIWYGBuilder">
      {/* Top toolbar */}
      <div className="WYSIWYGToolbar">
        <h3>Visual Template Builder</h3>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Changes auto-save. Click "Create Template" or "Update Template" above to save to database.
        </span>
      </div>

      <div className="WYSIWYGLayout">
        {/* Left: Component Library */}
        <div className="WYSIWYGPanel WYSIWYGLibrary">
          <h4>Components</h4>
          <p className="WYSIWYGHint">Drag components to add fields</p>
          <div className="WYSIWYGComponentList">
            {componentTypes.map((comp) => (
              <div
                key={comp.type}
                className="WYSIWYGComponent"
                draggable
                onDragStart={() => setDraggedComponent(comp)}
                onDragEnd={() => setDraggedComponent(null)}
              >
                <span className="WYSIWYGComponentIcon">{comp.icon}</span>
                <span className="WYSIWYGComponentLabel">{comp.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Center: Canvas */}
        <div className="WYSIWYGPanel WYSIWYGCanvas">
          <h4>Form Fields ({variables.length})</h4>

          <div
            className="WYSIWYGDropZone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDropVariable}
          >
            {variables.length === 0 ? (
              <div className="WYSIWYGEmpty">
                Drag components here to add form fields
              </div>
            ) : (
              variables.map((variable, idx) => (
                <div
                  key={variable.id}
                  className={`WYSIWYGField ${selectedVariable?.id === variable.id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedVariable(variable);
                    setSelectedTask(null); // Clear task selection
                  }}
                >
                  <div className="WYSIWYGFieldHeader">
                    <span className="WYSIWYGFieldIcon">
                      {componentTypes.find(c => c.type === variable.type)?.icon}
                    </span>
                    <span className="WYSIWYGFieldLabel">{variable.label}</span>
                    <span className="WYSIWYGFieldType">({variable.type})</span>
                    {variable.required && (
                      <span className="WYSIWYGFieldRequired">*</span>
                    )}
                  </div>
                  <div className="WYSIWYGFieldId">ID: {variable.id}</div>
                  <button
                    className="WYSIWYGFieldDelete"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteVariable(variable.id);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Task Builder */}
          <div className="WYSIWYGTaskSection">
            <div className="WYSIWYGTaskHeader">
              <h4>Tasks ({tasks.length})</h4>
              <button className="Btn BtnSecondary" onClick={addTask}>
                + Add Task
              </button>
            </div>

            <div className="WYSIWYGTaskList">
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className={`WYSIWYGTask ${selectedTask?.id === task.id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedTask(task);
                    setSelectedVariable(null); // Clear variable selection
                  }}
                >
                  <div className="WYSIWYGTaskTitle">{task.title}</div>
                  {task.condition && (
                    <div className="WYSIWYGTaskCondition">
                      If: {task.condition}
                    </div>
                  )}
                  <button
                    className="WYSIWYGTaskDelete"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteTask(task.id);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Properties Panel */}
        <div className="WYSIWYGPanel WYSIWYGProperties">
          <h4>Properties</h4>

          {selectedVariable ? (
            <VariableProperties
              variable={selectedVariable}
              onUpdate={(updates) => updateVariable(selectedVariable.id, updates)}
            />
          ) : selectedTask ? (
            <TaskProperties
              task={selectedTask}
              variables={variables}
              onUpdate={(updates) => updateTask(selectedTask.id, updates)}
              onInsertVariable={insertVariableIntoTask}
            />
          ) : (
            <div className="WYSIWYGEmpty">
              Select a field or task to edit properties
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Variable Properties Editor
 */
function VariableProperties({ variable, onUpdate }) {
  return (
    <div className="WYSIWYGPropsForm">
      <div className="WYSIWYGPropField">
        <label>Field ID</label>
        <input
          type="text"
          value={variable.id}
          onChange={(e) => onUpdate({ id: e.target.value })}
          placeholder="field_name"
        />
      </div>

      <div className="WYSIWYGPropField">
        <label>Label *</label>
        <input
          type="text"
          value={variable.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
          placeholder="Field label"
        />
      </div>

      <div className="WYSIWYGPropField">
        <label>Description</label>
        <textarea
          value={variable.description || ''}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="Help text for users"
          rows={2}
        />
      </div>

      <div className="WYSIWYGPropField">
        <label className="WYSIWYGCheckboxLabel">
          <input
            type="checkbox"
            checked={variable.required || false}
            onChange={(e) => onUpdate({ required: e.target.checked })}
          />
          <span>Required field</span>
        </label>
      </div>

      {/* Type-specific properties */}
      {variable.type === 'text' && (
        <div className="WYSIWYGPropField">
          <label>Placeholder</label>
          <input
            type="text"
            value={variable.placeholder || ''}
            onChange={(e) => onUpdate({ placeholder: e.target.value })}
            placeholder="e.g., Enter project name..."
          />
        </div>
      )}

      {variable.type === 'number' && (
        <>
          <div className="WYSIWYGPropField">
            <label>Minimum Value</label>
            <input
              type="number"
              value={variable.min || 0}
              onChange={(e) => onUpdate({ min: parseInt(e.target.value) })}
            />
          </div>
          <div className="WYSIWYGPropField">
            <label>Maximum Value</label>
            <input
              type="number"
              value={variable.max || 100}
              onChange={(e) => onUpdate({ max: parseInt(e.target.value) })}
            />
          </div>
        </>
      )}

      {(variable.type === 'dropdown' || variable.type === 'checkbox_group') && (
        <div className="WYSIWYGPropField">
          <label>Options (one per line)</label>
          <textarea
            value={(variable.options || []).join('\n')}
            onChange={(e) => {
              // Don't filter out empty lines while typing - only split on newlines
              const lines = e.target.value.split('\n');
              onUpdate({ options: lines });
            }}
            onBlur={(e) => {
              // Clean up empty lines when user is done editing
              const lines = e.target.value.split('\n').filter(line => line.trim() !== '');
              onUpdate({ options: lines });
            }}
            placeholder="Option 1&#10;Option 2&#10;Option 3"
            rows={5}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Task Properties Editor
 */
function TaskProperties({ task, variables, onUpdate, onInsertVariable }) {
  return (
    <div className="WYSIWYGPropsForm">
      <div className="WYSIWYGPropField">
        <label>Task Title *</label>
        <input
          type="text"
          value={task.title}
          onChange={(e) => onUpdate({ title: e.target.value })}
          placeholder="Task title"
        />
        <div className="WYSIWYGVariableHint">
          Insert variable:
          {variables.map((v) => (
            <button
              key={v.id}
              className="WYSIWYGVariableBtn"
              onClick={() => onInsertVariable(v.id)}
            >
              {v.id}
            </button>
          ))}
        </div>
      </div>

      <div className="WYSIWYGPropField">
        <label>Description</label>
        <textarea
          value={task.description || ''}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="Task description (supports HTML)"
          rows={4}
        />
      </div>

      <div className="WYSIWYGPropField">
        <label>Status</label>
        <select
          value={task.status || 'todo'}
          onChange={(e) => onUpdate({ status: e.target.value })}
        >
          <option value="todo">To Do</option>
          <option value="doing">Doing</option>
          <option value="done">Done</option>
        </select>
      </div>

      <div className="WYSIWYGPropField">
        <label>Condition (optional)</label>
        <input
          type="text"
          value={task.condition || ''}
          onChange={(e) => onUpdate({ condition: e.target.value })}
          placeholder="e.g., priority == 'High'"
        />
        <p className="WYSIWYGHint">
          Leave empty to always include. Use JavaScript syntax:
          <br />• variable {'=='} 'value'
          <br />• variable {'>'} 5
          <br />• array.includes('item')
        </p>
      </div>
    </div>
  );
}
